import { motion } from "framer-motion";
import { Zap, PackageCheck } from "lucide-react";
import { PLANS } from "@/constants/plans";
import { useStore } from "@/context/StoreContext";

const ease = [0.16, 1, 0.3, 1];

export const Pricing = () => {
  const { openOrder } = useStore();

  return (
    <section id="abonnement" className="relative py-28 sm:py-36 grid-bg overflow-hidden" data-testid="pricing-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-cyan-500/[0.05] blur-[140px]" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="text-center mb-16"
        >
          <p className="mono text-xs tracking-[0.3em] uppercase text-cyan-400 mb-5">Abonnement</p>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Velg din <span className="text-cyan-400">periode</span>
          </h2>
          <p className="text-slate-400 mt-5 max-w-xl mx-auto text-base sm:text-lg">
            Jo lenger periode, jo lavere månedspris. Betaling skjer enkelt via eFaktura.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.85, delay: i * 0.12, ease }}
              data-testid={`pricing-card-${plan.id}`}
              className={`relative rounded-3xl p-8 sm:p-9 flex flex-col transition-all duration-500 ${
                plan.popular
                  ? "bg-[#0F1623] border-2 border-cyan-400/50 shadow-[0_0_60px_rgba(0,229,255,0.12)] md:-translate-y-4"
                  : "bg-[#0F1623]/70 border border-white/[0.08] hover:border-white/20"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-cyan-400 text-[#06090E] mono text-[10px] font-semibold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full" data-testid="pricing-popular-badge">
                  <Zap size={12} /> Mest valgt
                </div>
              )}

              <h3 className="font-display font-bold text-2xl">{plan.name}</h3>
              <p className="text-slate-500 text-sm mt-1 mb-7">{plan.tagline}</p>

              <div className="mb-2">
                <span className="font-display font-extrabold text-5xl tracking-tight" data-testid={`pricing-price-${plan.id}`}>
                  {plan.total},-
                </span>
                <span className="text-slate-500 text-sm ml-2">totalt</span>
              </div>
              <p className="mono text-xs tracking-[0.15em] text-cyan-400/90 uppercase mb-8" data-testid={`pricing-permonth-${plan.id}`}>
                Tilsvarer {plan.perMonth},- / mnd
              </p>

              <div className="flex items-center gap-3 rounded-2xl bg-cyan-400/[0.06] border border-cyan-400/20 px-4 py-3.5 mb-10" data-testid={`pricing-included-${plan.id}`}>
                <PackageCheck size={18} className="text-cyan-400 shrink-0" />
                <p className="text-sm text-slate-300">Alle funksjoner inkludert</p>
              </div>

              <button
                onClick={() => openOrder(plan.id)}
                data-testid={`order-plan-${plan.id}-button`}
                className={`mt-auto w-full font-display font-bold py-3.5 rounded-full transition-all duration-300 hover:-translate-y-0.5 ${
                  plan.popular
                    ? "bg-[#FF4800] hover:bg-[#ff5d1f] text-white hover:shadow-[0_0_32px_rgba(255,72,0,0.45)]"
                    : "border border-white/15 text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300"
                }`}
              >
                Velg {plan.name}
              </button>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-slate-600 text-sm mt-12" data-testid="pricing-note">
          Alle priser er inkl. mva. Betaling skjer via eFaktura fra vårt regnskapsprogram – ingen kortopplysninger nødvendig.
        </p>
      </div>
    </section>
  );
};
